import {
  DEFAULT_PERIOD_SETTINGS,
  formatDate,
  formatDateTime,
  localIsoDay,
  periodMonthSchema,
  periodReopenSchema,
  periodSettingsInputSchema,
  periodSettingsSchema,
  periodYearSchema,
  type PeriodMonth,
  type PeriodSettings,
} from '@fluxus/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, Lock, LockOpen } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

export const periodsKey = (organizationId: string, year: number) => ['periods', organizationId, year];

/**
 * Configurações › Fechamento de períodos (T-066, RF-54, RN-06): os meses do ano com a situação de
 * fechamento, fechar e reabrir à mão (quem administra Metas e Resultados) e as regras do fechamento
 * automático. Mês fechado: alterar metas e resultados dele exige justificativa e fica na auditoria.
 */
export function PeriodosPage() {
  const { organizationId = '' } = useParams();
  const { client } = useAuth();
  const { projects } = useCurrentProject(organizationId);
  const currentYear = Number(localIsoDay().slice(0, 4));
  const [year, setYear] = useState(currentYear);
  const yearId = useId();
  // Anos dos projetos da empresa (o ano corrente sempre aparece).
  const years = [
    ...new Set([
      currentYear,
      ...projects.flatMap((p) =>
        Array.from({ length: p.horizonEnd - p.horizonStart + 1 }, (_, i) => p.horizonStart + i),
      ),
    ]),
  ].sort((a, b) => a - b);
  const data = useQuery({
    queryKey: periodsKey(organizationId, year),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/periods`, {
        query: { year },
        schema: periodYearSchema,
        signal,
      }),
  });

  return (
    <PageContainer className="max-w-3xl">
      <PageHeader
        title="Fechamento de períodos"
        description="Mês fechado não muda sem justificativa: lançar ou alterar metas e resultados dele pede o motivo e fica registrado na auditoria como período encerrado."
      />
      <div className="mt-4 flex items-center gap-2">
        <label htmlFor={yearId} className="text-sm font-medium">
          Ano
        </label>
        <NativeSelect
          id={yearId}
          value={String(year)}
          onChange={(e) => setYear(Number(e.target.value))}
          className="w-28"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </NativeSelect>
      </div>
      {data.isPending ? (
        <LoadingState className="mt-6" rows={6} label="Carregando os meses" />
      ) : data.isError ? (
        <ErrorState
          className="mt-6"
          error={data.error}
          onRetry={() => void data.refetch()}
          retrying={data.isFetching}
        />
      ) : (
        <div className="mt-6 flex flex-col gap-6">
          <PeriodSettingsSection
            key={data.data.settings.updatedAt ?? 'padrao'}
            organizationId={organizationId}
            initial={data.data.settings}
          />
          <MonthsSection organizationId={organizationId} year={year} months={data.data.months} />
        </div>
      )}
    </PageContainer>
  );
}

export function PeriodSettingsSection({
  organizationId,
  initial,
}: {
  organizationId: string;
  initial: PeriodSettings;
}) {
  const { client } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const canAdmin = can('metas-resultados', 'admin');
  const [autoClose, setAutoClose] = useState(initial.autoClose);
  const [days, setDays] = useState(String(initial.autoCloseDays));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const daysId = useId();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = periodSettingsInputSchema.safeParse({ autoClose, autoCloseDays: days.trim() });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    try {
      await client.request(`/organizations/${organizationId}/period-settings`, {
        method: 'PUT',
        body: parsed.data,
        schema: periodSettingsSchema,
      });
      toast.success('Regras do fechamento salvas');
      await queryClient.invalidateQueries({ queryKey: ['periods', organizationId] });
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      noValidate
      onSubmit={submit}
      aria-label="Fechamento automático"
      className="rounded-lg border border-border bg-surface p-5"
    >
      <h2 className="text-base font-bold">Fechamento automático</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {initial.updatedAt
          ? `Regras alteradas em ${formatDateTime(initial.updatedAt)}.`
          : `Vale o padrão: o mês fecha sozinho ${DEFAULT_PERIOD_SETTINGS.autoCloseDays} dias depois de terminar.`}
      </p>
      {!canAdmin ? (
        <p className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Só a equipe Fluxus (quem administra Metas e Resultados) fecha, reabre e muda estas regras.
        </p>
      ) : null}
      <label className="mt-4 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 accent-(--brand)"
          checked={autoClose}
          disabled={!canAdmin}
          onChange={(e) => setAutoClose(e.target.checked)}
        />
        <span>
          <span className="font-medium">Fechar os meses automaticamente</span>
          <span className="block text-xs text-muted-foreground">
            Um job diário fecha o mês passados os dias abaixo do fim dele. Mês reaberto à mão não volta a
            fechar sozinho.
          </span>
        </span>
      </label>
      <div className="mt-4">
        <FormField
          id={daysId}
          label="Dias após o fim do mês"
          error={errors.autoCloseDays}
          hint={<p className="text-xs text-muted-foreground">De 0 a 120. Com 10, março fecha em 11/04.</p>}
        >
          <Input
            {...fieldA11y(daysId, errors.autoCloseDays, true)}
            value={days}
            inputMode="numeric"
            maxLength={3}
            disabled={!canAdmin || !autoClose}
            onChange={(e) => setDays(e.target.value)}
            className="h-11 w-28"
          />
        </FormField>
      </div>
      {formError ? (
        <div className="mt-4">
          <FormAlert tone="error">{errorMessage(formError)}</FormAlert>
        </div>
      ) : null}
      {canAdmin ? (
        <div className="mt-4 flex justify-end">
          <Button type="submit" loading={saving}>
            Salvar regras
          </Button>
        </div>
      ) : null}
    </form>
  );
}

/** Situação do mês em uma linha, para quem lê a lista. */
function monthStatusText(m: PeriodMonth): string {
  if (m.status === 'CLOSED') {
    const when = formatDateTime(m.closedAt!);
    return m.mode === 'AUTO'
      ? `Fechado automaticamente em ${when}`
      : `Fechado por ${m.closedByName} em ${when}`;
  }
  if (m.reopenedAt) return `Reaberto por ${m.reopenedByName} em ${formatDateTime(m.reopenedAt)}`;
  if (!m.ended)
    return m.autoCloseOn
      ? `Em andamento ou futuro · fecha sozinho em ${formatDate(m.autoCloseOn)}`
      : 'Em andamento ou futuro';
  return m.autoCloseOn ? `Aberto · fecha sozinho em ${formatDate(m.autoCloseOn)}` : 'Aberto';
}

function MonthsSection({
  organizationId,
  year,
  months,
}: {
  organizationId: string;
  year: number;
  months: PeriodMonth[];
}) {
  const can = useCan();
  const canAdmin = can('metas-resultados', 'admin');
  const [acting, setActing] = useState<{ month: PeriodMonth; action: 'close' | 'reopen' } | null>(null);
  const [busy, setBusy] = useState(false);
  const closedCount = months.filter((m) => m.status === 'CLOSED').length;

  return (
    <section aria-label="Meses do ano" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-bold">Meses de {year}</h2>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {closedCount === 0
            ? 'Nenhum mês fechado'
            : closedCount === 1
              ? '1 mês fechado'
              : `${closedCount} meses fechados`}
        </p>
      </div>
      {months.every((m) => !m.ended && m.status === 'OPEN') ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-8"
          icon={CalendarCheck}
          title="Nenhum mês terminou ainda"
          description={`Os meses de ${year} aparecem para fechamento quando terminam.`}
        />
      ) : null}
      <ul
        aria-label={`Fechamento dos meses de ${year}`}
        className="flex flex-col divide-y divide-border rounded-lg border border-border bg-surface"
      >
        {months.map((m) => (
          <li
            key={m.month}
            className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{m.label}</span>
                {m.status === 'CLOSED' ? (
                  <Badge variant="brand">
                    <Lock className="size-3" aria-hidden />
                    Fechado
                  </Badge>
                ) : (
                  <Badge variant="outline">Aberto</Badge>
                )}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{monthStatusText(m)}</p>
              {m.status === 'CLOSED' && m.closeReason ? (
                <p className="mt-0.5 text-xs text-muted-foreground">Motivo: {m.closeReason}</p>
              ) : null}
              {m.status === 'OPEN' && m.reopenReason ? (
                <p className="mt-0.5 text-xs text-muted-foreground">Motivo da reabertura: {m.reopenReason}</p>
              ) : null}
            </div>
            {canAdmin ? (
              m.status === 'CLOSED' ? (
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Reabrir ${m.label}`}
                  onClick={() => setActing({ month: m, action: 'reopen' })}
                >
                  <LockOpen aria-hidden />
                  Reabrir
                </Button>
              ) : m.ended ? (
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Fechar ${m.label}`}
                  onClick={() => setActing({ month: m, action: 'close' })}
                >
                  <Lock aria-hidden />
                  Fechar
                </Button>
              ) : null
            ) : null}
          </li>
        ))}
      </ul>
      <Dialog
        open={acting !== null}
        onOpenChange={(open) => !open && setActing(null)}
        busy={busy}
        title={acting ? `${acting.action === 'close' ? 'Fechar' : 'Reabrir'} ${acting.month.label}` : ''}
      >
        {acting ? (
          <MonthActionForm
            organizationId={organizationId}
            year={year}
            month={acting.month}
            action={acting.action}
            onBusyChange={setBusy}
            onDone={() => setActing(null)}
          />
        ) : null}
      </Dialog>
    </section>
  );
}

function MonthActionForm({
  organizationId,
  year,
  month,
  action,
  onBusyChange,
  onDone,
}: {
  organizationId: string;
  year: number;
  month: PeriodMonth;
  action: 'close' | 'reopen';
  onBusyChange: (busy: boolean) => void;
  onDone: () => void;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const id = useId();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    if (action === 'reopen') {
      const parsed = periodReopenSchema.safeParse({ reason });
      if (!parsed.success) return setError(parsed.error.issues[0]?.message);
    }
    setError(undefined);
    setSaving(true);
    onBusyChange(true);
    try {
      const saved = await client.request(
        `/organizations/${organizationId}/periods/${year}/${month.month}/${action}`,
        { method: 'POST', body: { reason: reason.trim() || null }, schema: periodMonthSchema },
      );
      toast.success(action === 'close' ? `${saved.label} fechado` : `${saved.label} reaberto`);
      await queryClient.invalidateQueries({ queryKey: ['periods', organizationId] });
      onDone();
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (fields.reason) setError(fields.reason);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {action === 'close'
          ? `Depois de fechado, lançar ou alterar metas e resultados de ${month.label} exige justificativa e fica registrado na auditoria como período encerrado.`
          : `${month.label} volta a aceitar lançamentos e metas sem justificativa. O fechamento automático não o fecha de novo: feche à mão quando os ajustes terminarem.`}
      </p>
      <FormField
        id={id}
        label={action === 'close' ? 'Motivo (opcional)' : 'Motivo da reabertura'}
        error={error}
      >
        <textarea
          {...fieldA11y(id, error)}
          value={reason}
          maxLength={1000}
          rows={3}
          onChange={(e) => setReason(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onDone} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {action === 'close' ? 'Fechar mês' : 'Reabrir mês'}
        </Button>
      </div>
    </form>
  );
}
